import React, {useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {withRouter, useRouteUnmountEffect} from '@computerrock/formation-router';
import {useTranslate} from '@computerrock/formation-i18n';
import {useStyles, ContentBlock, ContentItem, Panel} from '@ace-de/ui-components';
import {Form, InputField} from '@ace-de/ui-components/form';
import ContactPersonDetailsPanel from '../service-assignments/ui-elements/ContactPersonDetailsPanel';
import RelevantLocationPanel from '../service-assignments/ui-elements/RelevantLocationPanel';
import * as serviceCaseSelectors from '../service-cases/serviceCaseSelectors';
import * as serviceAssignmentSelectors from '../service-assignments/serviceAssignmentSelectors';
import * as samsActionTypes from './samsActionTypes';
import mapMemberDataToContactPersonData from '../service-cases/mapMemberDataToContactPersonData';

const isObjectWithKeys = obj => (typeof obj === 'object' && !!Object.keys(obj).length);

const SAMSRequirementsTab = props => {
    const {cx} = useStyles();
    const {createTranslateShorthand} = useTranslate();
    const translateTab = createTranslateShorthand('sams_requirements_tab');
    const {serviceCase, serviceAssignment, submitSAMSRequirementsForm, submitSAMSServiceLocationForm} = props;
    const [formData, setFormData] = useState({
        // set initial form state in order to patch default values if an agent does not touch the form
        contactPersonOnSite: serviceAssignment?.contactPersonOnSite
            || mapMemberDataToContactPersonData(serviceCase.member),
        affectedPerson: serviceAssignment?.affectedPerson || mapMemberDataToContactPersonData(serviceCase.member),
    });

    const handleOnChange = formValues => {
        setFormData({
            ...formValues,
            ...(isObjectWithKeys(formValues['contactPersonOnSite']) && {
                contactPersonOnSite: {
                    ...(formValues['contactPersonOnSite']['isIdenticalToMember']
                        ? mapMemberDataToContactPersonData(serviceCase.member)
                        : formValues['contactPersonOnSite']),
                    isIdenticalToMember: !!formValues['contactPersonOnSite']['isIdenticalToMember'],
                },
            }),
            ...(isObjectWithKeys(formValues['affectedPerson']) && {
                affectedPerson: {
                    ...(formValues['affectedPerson']['isIdenticalToMember']
                        ? mapMemberDataToContactPersonData(serviceCase.member)
                        : formValues['affectedPerson']),
                    isIdenticalToMember: !!formValues['affectedPerson']['isIdenticalToMember'],
                },
            }),
        });
    };

    const handleLocationSaving = locationInfo => {
        if (!locationInfo) return;
        const isInitialAssignment = serviceAssignment.lineNo === 'FBS1'
            || parseInt(serviceAssignment.lineNo, 10) === 1 || serviceAssignment.isCloneFromInitial;

        submitSAMSServiceLocationForm({
            serviceAssignmentLineNo: serviceAssignment.lineNo,
            caseType: serviceCase.caseType,
            serviceCaseData: {
                // if it's an initial assignment, update damage location
                ...(isInitialAssignment && {
                    damage: {
                        location: {
                            ...(locationInfo.selectedLocation || {}),
                            locationNotes: locationInfo.locationNotes || '',
                        },
                    },
                }),
            },
            serviceCaseId: serviceCase.id,
            serviceAssignmentData: {
                serviceLocation: {
                    ...(locationInfo.selectedLocation || {}),
                    locationNotes: locationInfo.locationNotes || '',
                },
            },
        });
    };

    const handleOnTabChange = useCallback(formValues => {
        const {contactPersonOnSite, affectedPerson, medicineData} = formValues;
        submitSAMSRequirementsForm({
            serviceCaseId: serviceCase.id,
            serviceAssignmentLineNo: serviceAssignment.lineNo,
            serviceAssignmentData: {
                ...(isObjectWithKeys(contactPersonOnSite)
                    ? {contactPersonOnSite}
                    : (!serviceAssignment.contactPersonOnSite
                        ? {contactPersonOnSite: mapMemberDataToContactPersonData(serviceCase.member)}
                        : {})
                ),
                ...(isObjectWithKeys(affectedPerson)
                    ? {affectedPerson}
                    : (!serviceAssignment.affectedPerson
                        ? {affectedPerson: mapMemberDataToContactPersonData(serviceCase.member)}
                        : {})
                ),
                ...(isObjectWithKeys(medicineData) && medicineData),
            },
        });
    }, [
        serviceCase.id,
        serviceCase.member,
        serviceAssignment.lineNo,
        serviceAssignment.contactPersonOnSite,
        serviceAssignment.affectedPerson,
        submitSAMSRequirementsForm,
    ]);

    useRouteUnmountEffect(({completeRouteUnmountSideEffect}) => {
        if (!formData) {
            completeRouteUnmountSideEffect({
                caller: samsActionTypes.SUBMIT_SAMS_REQUIREMENTS_FORM,
            });
            return;
        }

        handleOnTabChange(formData);
    }, [formData, handleOnTabChange]);

    // if no service case or service assignment don't render
    if (!serviceCase || !serviceAssignment) return null;

    return (
        <ContentBlock>
            <ContentItem className={cx('ace-c-content-item--span-9')}>
                <Form name="contactPersonsForm" onChange={handleOnChange}>
                    <ContactPersonDetailsPanel
                        title={translateTab('panel_title.local_contact')}
                        formName="contactPersonOnSite"
                        isIdenticalToMember={true}
                        contactPerson={serviceAssignment.contactPersonOnSite}
                    />
                    <div className={cx('global!ace-u-margin--top-24')}>
                        <ContactPersonDetailsPanel
                            title={translateTab('panel_title.affected')}
                            formName="affectedPerson"
                            isIdenticalToMember={true}
                            hasDate={true}
                            contactPerson={serviceAssignment.affectedPerson}
                        />
                    </div>
                    <Panel title={translateTab('panel_title.medicine_needed')}>
                        <Form name="medicineData">
                            <div className={cx('global!ace-u-grid')}>
                                <InputField
                                    className={cx('global!ace-u-grid-column--span-8')}
                                    name="medicineName"
                                    value={serviceAssignment.medicineName}
                                    label={translateTab('input_label.medicine')}
                                />
                            </div>
                        </Form>
                    </Panel>
                </Form>
            </ContentItem>
            <ContentItem className={cx('ace-c-content-item--span-3')}>
                <RelevantLocationPanel
                    mapName="sams-member-location"
                    hasPreviousLocations={false}
                    handleLocationSaving={handleLocationSaving}
                    hasLocationNotes={true}
                    hasConfirmation={false}
                />
            </ContentItem>
        </ContentBlock>
    );
};

SAMSRequirementsTab.propTypes = {
    serviceCase: PropTypes.object,
    serviceAssignment: PropTypes.object,
    submitSAMSRequirementsForm: PropTypes.func.isRequired,
    submitSAMSServiceLocationForm: PropTypes.func.isRequired,
};

SAMSRequirementsTab.defaultProps = {
    serviceCase: null,
    serviceAssignment: null,
};

const mapStateToProps = (state, props) => {
    const serviceCaseSelector = serviceCaseSelectors.createServiceCaseSelector();
    const serviceAssignmentSelector = serviceAssignmentSelectors.createServiceAssignmentSelector();
    return {
        serviceCase: serviceCaseSelector(state, props),
        serviceAssignment: serviceAssignmentSelector(state, props),
    };
};

const mapDispatchToProps = dispatch => ({
    submitSAMSRequirementsForm: payload => dispatch({
        type: samsActionTypes.SUBMIT_SAMS_REQUIREMENTS_FORM,
        payload,
    }),
    submitSAMSServiceLocationForm: payload => dispatch({
        type: samsActionTypes.SUBMIT_SAMS_SERVICE_LOCATION_FORM,
        payload,
    }),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SAMSRequirementsTab));
